import { Loading, useFetch } from "@4uhub/lib4uhub";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import FormInformation from "../../../../../components/FormInformation/FormInformation";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getSalesContractPayerFinantialByPayerId } from "../../../../../services/salesContractPayerFinantial.service";
import { memo, useCallback, useEffect, useState } from "react";
import { ISalesContractPayerFinantialModal } from "../../../../../models/sales-contract-payer-finantial";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";

const translationPath =
  "page.4uSalesContract.registers.salesContractPayerFinantial.";

export const SalesContractPayerFinantialDisabled = memo(
  ({ payerId }: { payerId: string }) => {
    const {
      i18n: { language },
      t,
    } = useTranslation();

    const [
      dataSalesContractPayerFinantial,
      setDataSalesContractPayerFinantial,
    ] = useState<ISalesContractPayerFinantialModal>();

    const { sendRequest: get, loading } = useFetch(
      getSalesContractPayerFinantialByPayerId
    );

    const getHandler = useCallback(async () => {
      const { data, success } = await get({ SalesContractPayerId: payerId });
      if (data) {
        setDataSalesContractPayerFinantial(data);
      }
      return { data, success };
    }, [get, payerId]);

    useEffect(() => {
      getHandler();
    }, [getHandler]);

    const sortedItems = (dataSalesContractPayerFinantial?.items ?? []).sort(
      (a, b) => Number(b.enable) - Number(a.enable)
    );

    return (
      <>
        {loading && (
          <Loading sx={{ position: "relative", width: 20 }} size={20} />
        )}
        {sortedItems.map((payerFinantial, index) => (
          <Accordion key={index}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
            >
              <Stack direction="row" gap={1} alignItems="center">
                {payerFinantial.enable && (
                  <CheckCircleIcon sx={{ color: "green" }} />
                )}
                <Typography sx={{ fontWeight: "bold" }}>
                  {payerFinantial.bilingMethod.name || "-"}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <FormInformation
                    label={t(translationPath + "biling_method")}
                    data={payerFinantial.bilingMethod.name || "-"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInformation
                    label={t(translationPath + "mail") || "Mail"}
                    data={payerFinantial.mail || "-"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInformation
                    label={t(translationPath + "phone") || "Phone"}
                    data={payerFinantial.phone || "-"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInformation
                    label={
                      t(translationPath + "whatsapp_number") ||
                      "Whatsapp Number"
                    }
                    data={payerFinantial.whatsappNumber || "-"}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormInformation
                    label={
                      t(translationPath + "initial_date") || "Initial Date"
                    }
                    data={
                      payerFinantial?.initialDate
                        ? (() => {
                            const date = new Date(payerFinantial.initialDate);
                            date.setHours(
                              date.getHours() + date.getTimezoneOffset() / 60
                            );
                            return format(date, "P", {
                              locale:
                                language === "pt-BR"
                                  ? ptBR
                                  : language === "es-ES"
                                  ? es
                                  : enUS,
                            });
                          })()
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormInformation
                    label={t(translationPath + "end_date") || "End Date"}
                    data={
                      payerFinantial?.endDate
                        ? (() => {
                            const date = new Date(payerFinantial.endDate);
                            date.setHours(
                              date.getHours() + date.getTimezoneOffset() / 60
                            );
                            return format(date, "P", {
                              locale:
                                language === "pt-BR"
                                  ? ptBR
                                  : language === "es-ES"
                                  ? es
                                  : enUS,
                            });
                          })()
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormInformation
                    label={
                      t(translationPath + "monthly_payment_due_date") ||
                      "Monthly Payment Due Date"
                    }
                    data={
                      payerFinantial.monthlyPaymentDueDate.toString() || "-"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormInformation
                    label={t(translationPath + "observation") || "Observation"}
                    data={payerFinantial.observation || "-"}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </>
    );
  }
);
